/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

import React from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
// Redux Toolkit
import { useSelector } from "react-redux";
import { selectUser } from "../../../../store/userSlice";
// actions
import { singleResource } from "../../../../actions";
// BASE Components
import BASESvgIcon from "../../../../../@BASE/core/BASESvgIcon";
import NavLinkAdapter from "../../../../../@BASE/core/NavLinkAdapter";
import authRoles from "../../../../auth/authRoles";
// Material-UI Components
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
// custom components
import ViewSubheader from "../../../../shared-components/crud/subheaders/ViewSubheader";
import PageSubheader from "./PageSubheader";

function PageHeader(props) {
  const {
    instance,
    details,
    resource,
    action,
    onSave,
    save_disabled,
    leftSidebarToggle,
    livePreview,
  } = props;
  const routeParams = useParams();
  const user = useSelector(selectUser);

  const handleSubheaderObject = (resource, label, value) => {
    const data = {
      resource: resource,
      label: label,
      value: value,
    };

    return data;
  };

  const handleEditInstanceIcon = (resource) => {
    switch (resource) {
      case "courses":
        return "heroicons-outline:view-grid";
      case "lessons":
        return "heroicons-outline:cube";
      case "sections":
        return "heroicons-outline:folder";
      case "pages":
        return "heroicons-outline:document";
      case "templates":
        return "heroicons-outline:template";
      case "organizations":
        return "heroicons-outline:office-building";
      default:
        return "heroicons-outline:cube";
    }
  };

  const handleSubheader = () => {
    const data = [];
    data[0] = handleSubheaderObject(
      "Project",
      instance?.section?.lesson?.course?.project?.label,
      instance?.section?.lesson?.course?.project?.title
    );
    data[1] = handleSubheaderObject(
      "Course",
      instance?.section?.lesson?.course?.label,
      instance?.section?.lesson?.course?.title
    );
    data[2] = handleSubheaderObject(
      "Lesson",
      instance?.section?.lesson?.label,
      instance?.section?.lesson?.title
    );
    data[3] = handleSubheaderObject(
      "Section",
      instance?.section?.label,
      instance?.section?.title
    );
    data[4] = handleSubheaderObject("Page", "Label", instance.label);
    data[5] = handleSubheaderObject("Page", "Number", instance.screen);
    data[6] = handleSubheaderObject("Previous", "Page", instance.back);
    data[7] = handleSubheaderObject("Next", "Page", instance.next);

    return <PageSubheader data={data} />;
  };

  // if loading, show loading spinner
  if (!details || !instance) {
    return <CircularProgress />;
  }

  return (
    <div className="p-24 sm:p-32 w-full">
      <div className="flex flex-col md:flex-row md:items-center">
        <motion.div
          className="grow"
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1, transition: { delay: 0.3 } }}
        >
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1, transition: { delay: 0.3 } }}
            className="flex items-center mb-16 md:mb-32"
          >
            <IconButton
              onClick={leftSidebarToggle}
              aria-label="toggle sidebar"
              className="text-white"
            >
              <BASESvgIcon>heroicons-outline:menu</BASESvgIcon>
            </IconButton>
            <Typography className="text-2xl md:text-3xl font-extrabold tracking-tight leading-none grow">
              {details.title}
            </Typography>
            <motion.div
              className="flex-none"
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1, transition: { delay: 0.3 } }}
            >
              {authRoles.editor.includes(user.role) && (
                <Button
                  variant="contained"
                  component={NavLinkAdapter}
                  to={`edit${
                    details?.parent?.id != null
                      ? `?p=${details?.parent?.id}`
                      : ""
                  }`}
                  sx={{
                    backgroundColor: "common.white",
                    color: "primary.dark",
                  }}
                >
                  <BASESvgIcon size={20}>
                    heroicons-outline:pencil-alt
                  </BASESvgIcon>
                  <span className="mx-8">Edit</span>
                </Button>
              )}
              <Button
                variant="contained"
                component={NavLinkAdapter}
                to="comments"
                sx={{
                  backgroundColor: "common.white",
                  color: "primary.dark",
                }}
                className="mx-8"
              >
                <BASESvgIcon size={20}>
                  heroicons-outline:chat-alt-2
                </BASESvgIcon>
                <span className="mx-8">Comments</span>
              </Button>
              {/* <Button
                component={NavLinkAdapter}
                to={livePreview ? `/pages/${routeParams.id}` : "live-preview"}
                variant="contained"
                sx={{
                  backgroundColor: "common.white",
                  color: "primary.dark",
                }}
              >
                <BASESvgIcon size={20}>
                  {livePreview
                    ? "heroicons-outline:document-text"
                    : "heroicons-outline:template"}
                </BASESvgIcon>
                <span className="mx-8">
                  {livePreview ? "Storyboard" : "Live Preview"}
                </span>
              </Button> */}
            </motion.div>
          </motion.div>
          {handleSubheader()}
        </motion.div>
      </div>
    </div>
  );
}

export default PageHeader;
